import {
  RELEASE_CHECK_AT_MOST_EVERY_MS,
  RELEASE_CHECK_EVERY_MS,
  isNewerBuild,
  mayReloadNow,
  mayReloadOnNavigate,
} from '~/utils/newRelease'

/**
 * BOB notices a new release by itself (Mark, 2026-10-16); the rule of when is in utils/newRelease.ts.
 *
 * Every five minutes while BOB is on screen, and on coming back to the tab, it asks for the build's
 * marker, `/_nuxt/builds/latest.json`, which Nuxt writes at each build with that build's id. The ask
 * is never answered from a cache (`no-store`, a fresh query each time, and nginx sends the file
 * no-cache), though everything else under /_nuxt/ is kept for a year.
 *
 * Once a newer build is out: the next move to another screen loads it in full, and a screen left
 * untouched (or a tab in the background) with nothing typed and no window open reloads on its own.
 * The New Version notice (components/app/NewReleaseNotice.vue) lets someone reload when they choose.
 */
export default defineNuxtPlugin((nuxtApp) => {
  // The dev server has no marker; a test run must not reload under its checks.
  if (import.meta.dev) return

  const config = useRuntimeConfig()
  const runningId = config.app.buildId
  const assetsDir = `${config.app.baseURL.replace(/\/$/, '')}/${config.app.buildAssetsDir.replace(/^\/|\/$/g, '')}`
  const markerUrl = `${assetsDir}/builds/latest.json`

  const newRelease = useState('new-release', () => false)
  const router = useRouter()

  // ── What the screen is doing ──
  let lastActivity = Date.now()
  let typedOnScreen = false
  const markActive = () => { lastActivity = Date.now() }
  for (const name of ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const) {
    window.addEventListener(name, markActive, { passive: true, capture: true })
  }
  // Typed or picked in a field: this screen holds someone's work until they leave it.
  const markTyped = (event: Event) => {
    markActive()
    const target = event.target as HTMLElement | null
    if (target && target.closest('input, textarea, select, [contenteditable="true"]')) typedOnScreen = true
  }
  window.addEventListener('input', markTyped, { capture: true })
  window.addEventListener('change', markTyped, { capture: true })

  const screenState = () => {
    return {
      newRelease: newRelease.value,
      windowOpen: !!document.querySelector('[role="dialog"], [aria-modal="true"]'),
      typedOnScreen,
      idleMs: Date.now() - lastActivity,
      hidden: document.visibilityState === 'hidden',
    }
  }

  const tryReload = () => {
    if (mayReloadNow(screenState())) window.location.reload()
  }

  // ── A move to another screen loads the new release ──
  router.beforeEach((to, from) => {
    if (!mayReloadOnNavigate(newRelease.value, from.path, to.path)) return
    window.location.assign(router.resolve(to).href)
    return false
  })
  router.afterEach((to, from) => {
    if (to.path !== from.path) typedOnScreen = false
  })

  // ── Asking for the marker ──
  let lastAsked = 0
  const check = async () => {
    lastAsked = Date.now()
    if (!newRelease.value) {
      try {
        const res = await fetch(`${markerUrl}?t=${Date.now()}`, { cache: 'no-store' })
        if (res.ok && isNewerBuild(runningId, await res.json())) newRelease.value = true
      } catch {
        // Offline or the server is busy: ask again next time.
      }
    }
    tryReload()
  }

  nuxtApp.hook('app:mounted', () => {
    window.setInterval(() => {
      if (document.visibilityState === 'visible') void check()
      else tryReload()
    }, RELEASE_CHECK_EVERY_MS)
    // Once a release is known, look for a quiet moment more often than the marker is asked for.
    window.setInterval(() => { if (newRelease.value) tryReload() }, 15_000)

    const onBack = () => {
      if (document.visibilityState !== 'visible') {
        tryReload()
        return
      }
      if (Date.now() - lastAsked >= RELEASE_CHECK_AT_MOST_EVERY_MS) void check()
    }
    document.addEventListener('visibilitychange', onBack)
    window.addEventListener('focus', onBack)
  })
})
